import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '@/pages/home/components/Navigation';
import Footer from '@/pages/home/components/Footer';

/* ─────────────────────────────────────────────
   SECTION – WATCH THE SLIDES
───────────────────────────────────────────── */
function SlidesSection() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  const scrollByCard = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = 442 + 20; // lg card width + gap
    el.scrollBy({ left: direction === 'left' ? -cardWidth : cardWidth, behavior: 'smooth' });
    setTimeout(checkScroll, 350);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener('scroll', checkScroll, { passive: true });
    return () => el.removeEventListener('scroll', checkScroll);
  }, []);

  const slides = [
    {
      src: 'https://storage.readdy-site.link/project_files/e0ccb82e-1efa-45a2-81a6-920d70939725/357daae0-4b2f-478d-a162-25792ece0c21_compressed_F1.webp',
      alt: 'Attention disappears faster than your team can respond',
    },
    {
      src: 'https://storage.readdy-site.link/project_files/e0ccb82e-1efa-45a2-81a6-920d70939725/79934290-a534-4471-aee3-771e6789289a_compressed_F2.webp',
      alt: 'Our system catches the lead within seconds',
    },
    {
      src: 'https://storage.readdy-site.link/project_files/e0ccb82e-1efa-45a2-81a6-920d70939725/b2c2cfbf-7a48-4187-91ef-9c6bbe0edd08_compressed_F3.webp',
      alt: 'It asks the right questions and understands intent',
    },
    {
      src: 'https://storage.readdy-site.link/project_files/e0ccb82e-1efa-45a2-81a6-920d70939725/2b58168a-1263-46e0-8cb9-7edc001b5d45_compressed_F4.webp',
      alt: 'It keeps momentum alive and gets site visits booked',
    },
    {
      src: 'https://storage.readdy-site.link/project_files/e0ccb82e-1efa-45a2-81a6-920d70939725/9e8e53ab-0765-410f-aebe-a7b1506878e5_compressed_F5.webp',
      alt: 'Complete buyer context before the first meeting',
    },
    {
      src: 'https://storage.readdy-site.link/project_files/e0ccb82e-1efa-45a2-81a6-920d70939725/fbca83fa-d641-4db0-8ce7-8eef9f6bc09f_compressed_F6.webp',
      alt: 'Insights into better meetings and stronger outcomes',
    },
    {
      src: 'https://storage.readdy-site.link/project_files/e0ccb82e-1efa-45a2-81a6-920d70939725/590598d7-ab0e-482a-9261-9e1375017b22_compressed_F7.webp',
      alt: 'Relationship stays active and warm after booking',
    },
    {
      src: 'https://storage.readdy-site.link/project_files/e0ccb82e-1efa-45a2-81a6-920d70939725/799354e6-a6bb-48a4-add8-e120c6f79fca_compressed_F8.webp',
      alt: 'One operating system powering everything',
    },
  ];

  return (
    <section id="re-slides" className="py-24 bg-black">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-semibold text-white tracking-tight mb-6">
            Watch the Slides.
          </h2>
        </div>
      </div>

      {/* Slides — full bleed, cards sink into screen edges */}
      <div className="relative">
        <div
          ref={scrollRef}
          className="flex gap-5 overflow-x-auto pb-6 snap-x snap-mandatory pl-8 sm:pl-8 lg:pl-16 pr-8 sm:pr-8 lg:pr-16"
          style={{ scrollbarWidth: 'none' }}
        >
          {slides.map((slide, idx) => (
            <div
              key={idx}
              className="flex-shrink-0 w-[286px] sm:w-[338px] md:w-[390px] lg:w-[442px] snap-center"
            >
              <div className="rounded-3xl overflow-hidden bg-white">
                <div className="hover:scale-[1.02] transition-transform duration-300">
                  <img
                    src={slide.src}
                    alt={slide.alt}
                    className="w-full h-auto object-contain object-top"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Arrow controls — Apple-style bottom-right chevrons */}
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-12">
          <div className="flex items-center justify-end gap-2 mt-2">
            <button
              onClick={() => scrollByCard('left')}
              className={`w-9 h-9 flex items-center justify-center rounded-full border border-gray-500 bg-white transition-all duration-200 cursor-pointer ${
                canScrollLeft
                  ? 'opacity-100 hover:bg-gray-200'
                  : 'opacity-30 cursor-default pointer-events-none'
              }`}
              aria-label="Scroll left"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-s-line text-lg text-gray-700"></i>
              </div>
            </button>
            <button
              onClick={() => scrollByCard('right')}
              className={`w-9 h-9 flex items-center justify-center rounded-full border border-gray-500 bg-white transition-all duration-200 cursor-pointer ${
                canScrollRight
                  ? 'opacity-100 hover:bg-gray-200'
                  : 'opacity-30 cursor-default pointer-events-none'
              }`}
              aria-label="Scroll right"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-lg text-gray-700"></i>
              </div>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   SECTION 1 – HERO
───────────────────────────────────────────── */
function HeroSection() {
  const navigate = useNavigate();

  const handleWatchDemo = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const el = document.getElementById('re-demo');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleWatchSlides = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const el = document.getElementById('re-slides');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleGetStarted = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const el = document.getElementById('re-contact');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section className="relative flex flex-col bg-white">
      {/* Hero content — plain white background */}
      <div className="relative flex flex-col items-center justify-center pt-24 pb-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">

          {/* Building image above headline */}
          <div className="w-full max-w-3xl mx-auto mb-8">
            <img
              src="https://storage.readdy-site.link/project_files/e0ccb82e-1efa-45a2-81a6-920d70939725/2afaaa57-3a83-42a7-9c61-3a0510dc6c5d_compressed_hsbg.webp"
              alt="Modern luxury real estate building"
              className="w-full h-auto object-contain"
            />
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-semibold text-gray-900 tracking-tight leading-[1.08] mb-10">
            Real Estate
            <br />
            <span className="bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 bg-clip-text text-transparent">
              AI System
            </span>
          </h1>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="#re-demo"
              onClick={handleWatchDemo}
              className="hidden group inline-flex items-center gap-3 bg-gray-900 hover:bg-black text-white px-8 py-4 rounded-full font-medium text-base transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-play-circle-line text-xl"></i>
              </div>
              <span>Watch the Demo</span>
            </a>
            <a
              href="#re-slides"
              onClick={handleWatchSlides}
              className="group inline-flex items-center gap-3 bg-gray-900 hover:bg-black text-white px-8 py-4 rounded-full font-medium text-base transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-presentation-line text-xl"></i>
              </div>
              <span>Watch the Slides</span>
            </a>
            <a
              href="#re-contact"
              onClick={handleGetStarted}
              className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium text-base transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              <span>Get Started</span>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </div>
            </a>
          </div>
        </div>
      </div>

      <SlidesSection />

      {/* Stats strip + subheadline */}
      <div className="bg-white py-32">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <p className="text-xl md:text-2xl text-gray-500 font-normal text-center max-w-2xl mx-auto leading-relaxed">
            From <strong className="text-gray-700 font-semibold">first customer inquiry</strong> to <strong className="text-gray-700 font-semibold">final payment</strong> — the complete end-to-end system for Real Estate.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   SECTION 2 – DEMO VIDEO
───────────────────────────────────────────── */
function DemoVideoSection() {
  return (
    <section id="re-demo" className="py-24 bg-white hidden">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

        <div className="text-center mb-20">
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-semibold text-black tracking-tight mb-6">
            Watch the Demo.
          </h2>
        </div>

        {/* Cinematic video player */}
        <div className="relative rounded-2xl overflow-hidden bg-black mb-6">
          <div className="relative aspect-video">
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
              <div className="relative group cursor-pointer">
                <div className="w-24 h-24 rounded-full flex items-center justify-center group-hover:scale-105 transition-all duration-300" style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
                  <div className="w-9 h-9 flex items-center justify-center ml-1.5">
                    <i className="ri-play-fill text-white text-4xl"></i>
                  </div>
                </div>
              </div>
            </div>

            <div className="absolute bottom-0 left-0 right-0 flex items-center justify-end px-5 py-4">
              <span className="text-white/60 text-xs font-mono">3:42</span>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   SECTION 3 – STATEMENT: THE HOOK
───────────────────────────────────────────── */
function StatementHookSection() {
  const [revealed, setRevealed] = useState<boolean[]>([false, false]);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          [0, 1].forEach((i) => {
            setTimeout(() => {
              setRevealed((prev) => {
                const next = [...prev];
                next[i] = true;
                return next;
              });
            }, i * 280);
          });
          obs.disconnect();
        }
      },
      { threshold: 0.25 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section className="py-36 md:py-44 bg-white relative overflow-hidden">
      {/* Pure white background — no tint overlays */}
      {/* Premium ambient glow orb behind text */}
      <div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] rounded-full pointer-events-none opacity-40"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(16,185,129,0.06) 0%, rgba(20,184,166,0.04) 40%, transparent 70%)',
          animation: 'glow-breathe 6s ease-in-out infinite',
        }}
      />

      <div
        ref={sectionRef}
        className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center flex flex-col items-center gap-6 md:gap-8"
      >
        <div
          className={`transition-all duration-1000 ease-[cubic-bezier(0.22,1,0.36,1)] ${
            revealed[0] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
        >
          <p className="text-3xl md:text-4xl lg:text-5xl font-semibold text-gray-900 leading-normal">
            <span className="inline-block pb-1">
              Selling hundreds of apartments takes more than ads.
            </span>
          </p>
        </div>
        <div
          className={`transition-all duration-1000 delay-200 ease-[cubic-bezier(0.22,1,0.36,1)] ${
            revealed[1] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
        >
          <p className="text-3xl md:text-4xl lg:text-5xl font-semibold leading-normal">
            <span
              className="inline-block pb-1 bg-clip-text text-transparent animate-iphone-shimmer"
              style={{
                backgroundImage: 'linear-gradient(90deg, #10b981, #14b8a6, #06b6d4, #10b981, #14b8a6, #06b6d4)',
              }}
            >
              It takes a system
            </span>
          </p>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   SECTION 4 – STATEMENT: THE CONTEXT
───────────────────────────────────────────── */
function StatementContextSection() {
  const [revealed, setRevealed] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setRevealed(true);
          obs.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section className="py-28 md:py-36 bg-black relative overflow-hidden">
      {/* Emerald accent line at top */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px bg-gradient-to-r from-transparent via-emerald-500 to-transparent" />

      <div
        ref={sectionRef}
        className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
      >
        <p
          className={`text-lg md:text-xl lg:text-2xl text-gray-400 leading-relaxed transition-all duration-1000 ease-out ${
            revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          From <strong className="text-white font-semibold">first ad click</strong> to <strong className="text-white font-semibold">final key handover</strong>, we build the tools that make every stage <strong className="text-white font-semibold">sharper</strong>, <strong className="text-white font-semibold">faster</strong>, and <strong className="text-white font-semibold">more profitable</strong>.
        </p>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   SECTION 5 – STATEMENT: THE BRIDGE
───────────────────────────────────────────── */
function StatementBridgeSection() {
  const [revealed, setRevealed] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setRevealed(true);
          obs.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section className="pt-8 pb-20 md:pb-28 bg-white relative overflow-hidden">
      {/* Very light grey gradient on top */}
      <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-gray-50 to-white pointer-events-none" />

      <div
        ref={sectionRef}
        className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center flex flex-col items-center gap-8"
      >
        <p
          className={`text-xl md:text-2xl font-semibold text-black tracking-tight transition-all duration-700 ease-out ${
            revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          A complete operating system for real-estate sales.
        </p>

        {/* Down arrow cue - subtle bounce animation */}
        <div
          className={`transition-all duration-700 delay-300 ease-out ${
            revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <div className="w-10 h-10 flex items-center justify-center animate-gentle-bounce">
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   SECTION – OUR CORE OFFERINGS
───────────────────────────────────────────── */
function CoreOfferingsSection() {
  const offerings = [
    {
      image: 'https://storage.readdy-site.link/project_files/e0ccb82e-1efa-45a2-81a6-920d70939725/6d149ea3-e043-4ae1-8c4a-9b71195616a3_compressed_o1.webp',
      title: 'AI Voice Agents',
      description:
        'Human-like inbound and outbound voice agents that answer calls, qualify leads, schedule appointments, and follow up automatically.',
      accentText: 'text-violet-500',
    },
    {
      image: 'https://storage.readdy-site.link/project_files/e0ccb82e-1efa-45a2-81a6-920d70939725/0bbeaa02-9ff1-4ba6-9b49-b43285337975_compressed_o2.webp',
      title: 'AI Chat Agents',
      description:
        'Intelligent AI conversations across WhatsApp, SMS, and other messaging channels to answer questions instantly and nurture leads.',
      accentText: 'text-emerald-500',
    },
    {
      image: 'https://storage.readdy-site.link/project_files/e0ccb82e-1efa-45a2-81a6-920d70939725/70c28199-a93a-46d6-987d-57f909b437cb_compressed_o3.webp',
      title: 'Workflow Automation',
      description:
        'Automate repetitive business processes, customer journeys, notifications, approvals, and internal workflows with precision.',
      accentText: 'text-blue-500',
    },
    {
      image: 'https://storage.readdy-site.link/project_files/e0ccb82e-1efa-45a2-81a6-920d70939725/321949c0-0324-4ed3-bbc4-e668fac5d23a_compressed_o4.webp',
      title: 'CRM & Lead Source Integrations',
      description:
        'Seamlessly connect with CRMs, Meta Ads, 99acres, MagicBricks, websites, calendars, and other business systems.',
      accentText: 'text-indigo-500',
    },
    {
      image: 'https://storage.readdy-site.link/project_files/e0ccb82e-1efa-45a2-81a6-920d70939725/5565cee6-fc74-45ef-aced-881650afb449_compressed_o5.webp',
      title: '24\u00d77 System Availability',
      description:
        'AI agents and automations operate around the clock, ensuring every inquiry is captured and no lead goes cold.',
      accentText: 'text-purple-500',
    },
  ];

  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [revealed, setRevealed] = useState<boolean[]>([false, false, false, false, false, false]);
  const sectionRef = useRef<HTMLDivElement>(null);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  const scrollByCard = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = 380 + 20;
    el.scrollBy({ left: direction === 'left' ? -cardWidth : cardWidth, behavior: 'smooth' });
    setTimeout(checkScroll, 350);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener('scroll', checkScroll, { passive: true });
    return () => el.removeEventListener('scroll', checkScroll);
  }, []);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          [0, 1, 2, 3, 4, 5].forEach((i) => {
            setTimeout(() => {
              setRevealed((prev) => {
                const next = [...prev];
                next[i] = true;
                return next;
              });
            }, i * 140);
          });
          obs.disconnect();
        }
      },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section className="py-32 md:py-44 bg-white relative overflow-hidden">
      {/* Ambient glow orb */}
      <div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] rounded-full pointer-events-none opacity-30"
        style={{
          background:
            'radial-gradient(ellipse at center, rgba(16,185,129,0.08) 0%, rgba(20,184,166,0.04) 40%, transparent 70%)',
          animation: 'glow-breathe 6s ease-in-out infinite',
        }}
      />

      <div ref={sectionRef} className="relative z-10">
        {/* Header */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className={`text-center mb-16 md:mb-20 transition-all duration-1000 ease-[cubic-bezier(0.22,1,0.36,1)] ${
              revealed[0] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-black tracking-tight mb-6">
              Our Core Offerings
            </h2>
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto font-normal">
              A complete AI-powered ecosystem to capture leads, engage buyers, and accelerate sales.
            </p>
          </div>
        </div>

        {/* Cards — full bleed horizontal scroll */}
        <div className="relative">
          <div
            ref={scrollRef}
            className="flex gap-5 overflow-x-auto pb-6 snap-x snap-mandatory pl-8 sm:pl-8 lg:pl-16 pr-8 sm:pr-8 lg:pr-16"
            style={{ scrollbarWidth: 'none' }}
          >
            {offerings.map((offering, i) => (
              <div
                key={offering.title}
                className={`flex-shrink-0 w-[300px] sm:w-[320px] md:w-[340px] lg:w-[380px] snap-center transition-all duration-1000 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                  revealed[i + 1] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                }`}
                style={{ transitionDelay: revealed[i + 1] ? `${i * 80}ms` : '0ms' }}
              >
                <div className="group bg-white rounded-3xl border border-gray-200 p-7 md:p-8 hover:border-gray-300 hover:shadow transition-all duration-500 ease-out cursor-default h-full flex flex-col">
                  {/* Icon image */}
                  <div className="w-32 h-32 md:w-40 md:h-40 rounded-full flex items-center justify-center mb-10 bg-gray-50 overflow-hidden mx-auto">
                    <img
                      src={offering.image}
                      alt={offering.title}
                      className="w-full h-full object-contain"
                    />
                  </div>

                  {/* Title */}
                  <h3 className="text-lg md:text-xl font-semibold text-black tracking-tight mb-3">
                    {offering.title}
                  </h3>

                  {/* Black dash */}
                  <div className="w-8 h-0.5 rounded-full bg-black mb-5 opacity-60" />

                  {/* Description */}
                  <p className="text-gray-600 leading-relaxed text-sm md:text-base font-normal flex-1">
                    {offering.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Arrow controls */}
          <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-12">
            <div className="flex items-center justify-end gap-2 mt-2">
              <button
                onClick={() => scrollByCard('left')}
                className={`w-9 h-9 flex items-center justify-center rounded-full border border-gray-200 bg-white transition-all duration-200 cursor-pointer ${
                  canScrollLeft
                    ? 'opacity-100 hover:bg-gray-50'
                    : 'opacity-30 cursor-default pointer-events-none'
                }`}
                aria-label="Scroll left"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-s-line text-lg text-gray-700"></i>
                </div>
              </button>
              <button
                onClick={() => scrollByCard('right')}
                className={`w-9 h-9 flex items-center justify-center rounded-full border border-gray-200 bg-white transition-all duration-200 cursor-pointer ${
                  canScrollRight
                    ? 'opacity-100 hover:bg-gray-50'
                    : 'opacity-30 cursor-default pointer-events-none'
                }`}
                aria-label="Scroll right"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line text-lg text-gray-700"></i>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   SECTION 8 – PRICING TEASER
───────────────────────────────────────────── */
function PricingSection() {
  const navigate = useNavigate();
  const [revealed, setRevealed] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setRevealed(true);
          obs.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section className="py-32 md:py-44 bg-gray-50 relative overflow-hidden">
      <div
        ref={sectionRef}
        className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 text-center"
      >
        {/* Headline — matching "How setup works." style */}
        <h2
          className={`text-4xl md:text-5xl lg:text-6xl font-semibold text-black tracking-tight mb-6 transition-all duration-1000 ease-[cubic-bezier(0.22,1,0.36,1)] ${
            revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
        >
          Our Pricing.
Pay for what you use.
        </h2>


        <p
          className={`text-base md:text-lg text-black max-w-lg mx-auto leading-relaxed mb-16 transition-all duration-1000 delay-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${
            revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          Get <strong className="font-semibold">every feature</strong> from day one. High-performance <strong className="font-semibold">AI Voice and Automation</strong> built for businesses that value their leads.
        </p>

        {/* CTA — matching Straightforward pricing section style */}
        <a
          href="/pricing"
          onClick={(e) => { e.preventDefault(); navigate('/pricing'); }}
          className={`inline-flex items-center justify-center space-x-2 bg-gray-900 hover:bg-black text-white px-10 py-4 rounded-full font-medium text-base transition-all duration-300 whitespace-nowrap cursor-pointer ${
            revealed ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ transitionDelay: revealed ? '500ms' : '0ms' }}
        >
          <span>View Detailed Pricing</span>
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-arrow-right-line text-lg"></i>
          </div>
        </a>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   SECTION 9 – SETUP STEPS
───────────────────────────────────────────── */
function SetupSection() {
  const steps = [
    {
      number: '01',
      title: 'Map your sales floor',
      description: (
        <>
          A focused call to understand your project, your <strong className="text-black font-semibold">top-performing scripts</strong>, and every <strong className="text-black font-semibold">buyer objection</strong> your team faces. No templates — built entirely around <strong className="text-black font-semibold">your launch</strong>.
        </>
      ),
    },
    {
      number: '02',
      title: 'Preview your AI agent',
      description: (
        <>
          Within <strong className="text-black font-semibold">3 days</strong>, we share a <strong className="text-black font-semibold">live demo</strong> of how your AI agent sounds and handles real buyer conversations — in <strong className="text-black font-semibold">fluent, localized Hinglish</strong>.
        </>
      ),
    },
    {
      number: '03',
      title: 'Connect your stack',
      description: (
        <>
          We integrate directly with your <strong className="text-black font-semibold">Meta Ads</strong>, <strong className="text-black font-semibold">WhatsApp Business API</strong>, and <strong className="text-black font-semibold">CRM</strong> — LeadSquared, Salesforce, or Zoho. <strong className="text-black font-semibold">Your team touches nothing.</strong>
        </>
      ),
    },
    {
      number: '04',
      title: 'Go live. Close more.',
      description: (
        <>
          Your <strong className="text-black font-semibold">automated sales engine</strong> launches on a <strong className="text-black font-semibold">dedicated high-speed line</strong> — qualifying leads and booking site visits <strong className="text-black font-semibold">24/7</strong> from day one.
        </>
      ),
    },
  ];

  const [revealed, setRevealed] = useState<boolean[]>([false, false, false, false, false]);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          [0, 1, 2, 3, 4].forEach((i) => {
            setTimeout(() => {
              setRevealed((prev) => {
                const next = [...prev];
                next[i] = true;
                return next;
              });
            }, i * 350);
          });
          obs.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section className="py-32 md:py-44 bg-white relative overflow-hidden">
      {/* Ambient glow orb */}
      <div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[500px] rounded-full pointer-events-none opacity-40"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(16,185,129,0.10) 0%, rgba(20,184,166,0.06) 40%, transparent 70%)',
          animation: 'glow-breathe 6s ease-in-out infinite',
        }}
      />

      <div ref={sectionRef} className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div
          className={`text-center mb-24 md:mb-32 transition-all duration-1000 ease-[cubic-bezier(0.22,1,0.36,1)] ${
            revealed[0] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
        >
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-black tracking-tight mb-6">
            How setup works.
          </h2>
          <p className="text-lg md:text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto font-normal">
            No retraining your team. No changing your tools. We wrap <strong className="text-black font-semibold">seamlessly</strong> around your <strong className="text-black font-semibold">existing sales process</strong>.
          </p>
        </div>

        {/* Steps — editorial vertical list */}
        <div className="flex flex-col gap-20 md:gap-28">
          {steps.map((step, i) => (
            <div
              key={step.number}
              className={`flex flex-col md:flex-row md:items-start gap-6 md:gap-12 transition-all duration-1000 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                revealed[i + 1] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`}
            >
              <div className="flex-shrink-0 md:w-28">
                <span className="text-5xl md:text-7xl font-bold text-gray-200 tracking-tight">
                  {step.number}
                </span>
              </div>
              <div className="flex-1">
                <h3 className="text-xl md:text-2xl lg:text-3xl font-semibold text-black tracking-tight mb-4">
                  {step.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-base md:text-lg max-w-xl font-normal">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   SECTION 10 – FAQ
───────────────────────────────────────────── */
function FAQSection() {
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  const faqs = [
    {
      q: 'Will it understand Indian names and accents?',
      a: 'Yes. Our system is specifically tuned for the Indian market. It handles local names, city pronunciations, and natural "Hinglish" effortlessly — no awkward pauses or mispronunciations.',
    },
    {
      q: 'What happens if a buyer asks a complex question?',
      a: 'The system handles it gracefully. It will say, "That is a great technical question regarding the floor plan. Let me note that down so our Senior Closing Manager can cover it during your site visit." The query is logged and your team is notified.',
    },
    {
      q: 'Can it transfer the call to a human?',
      a: 'Absolutely. If a high-value buyer is ready to speak with a manager immediately, the system can seamlessly route the live call to your on-duty sales director — no hold music, no awkward gaps.',
    },
    {
      q: 'Does it work with our existing CRM?',
      a: 'Yes. We natively integrate with LeadSquared, Salesforce, and Zoho CRM. Every call, qualification data point, and booked visit is automatically synced — no manual data entry required.',
    },
    {
      q: 'How quickly can we go live?',
      a: 'Most projects go live in 3–5 business days. The Blueprint call takes about 2 hours, integration is handled entirely by our team, and your engine starts qualifying leads on day one.',
    },
  ];

  return (
    <section className="py-28 bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-black tracking-tight mb-6">
            Common Questions.
          </h2>
        </div>

        {/* Accordion */}
        <div className="flex flex-col gap-3">
          {faqs.map((faq, idx) => (
            <div
              key={idx}
              className={`bg-gray-50 border rounded-2xl overflow-hidden transition-colors duration-200 ${
                openIdx === idx ? 'border-gray-300' : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <button
                className="w-full px-7 py-6 text-left flex items-center justify-between gap-4 cursor-pointer"
                onClick={() => setOpenIdx(openIdx === idx ? null : idx)}
              >
                <span className="text-base font-semibold text-black leading-snug">{faq.q}</span>
                <div className={`w-7 h-7 flex items-center justify-center flex-shrink-0 rounded-full border transition-all duration-200 ${
                  openIdx === idx ? 'bg-black border-black rotate-45' : 'border-gray-300'
                }`}>
                  <i className={`ri-add-line text-sm ${openIdx === idx ? 'text-white' : 'text-gray-500'}`}></i>
                </div>
              </button>
              {openIdx === idx && (
                <div className="px-7 pb-6">
                  <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   SECTION 12 – AI AGENT CTA (Re-page specific)
───────────────────────────────────────────── */
function REContactSection() {
  const [revealed, setRevealed] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setRevealed(true);
          obs.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section id="re-contact" className="py-40 md:py-52 bg-gray-950 relative overflow-hidden">
      {/* Ambient emerald glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[500px]"
          style={{ background: 'radial-gradient(ellipse at center, rgba(16,185,129,0.08) 0%, rgba(20,184,166,0.04) 40%, transparent 70%)' }}
        />
      </div>

      <div
        ref={sectionRef}
        className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
      >
        {/* Live indicator */}
        <div
          className={`inline-flex items-center gap-2.5 mb-8 transition-all duration-700 ease-out ${
            revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500" />
          </span>
          <span className="text-sm font-medium text-gray-400 tracking-wide">Available now</span>
        </div>

        {/* Title — matching "Common Questions." style */}
        <h2
          className={`text-4xl md:text-5xl lg:text-6xl font-semibold text-white tracking-tight mb-6 transition-all duration-1000 ease-[cubic-bezier(0.22,1,0.36,1)] ${
            revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
        >
          Contact Us
        </h2>

        {/* Subtitle */}
        <div
          className={`flex flex-col gap-1 transition-all duration-1000 delay-200 ease-[cubic-bezier(0.22,1,0.36,1)] ${
            revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p className="text-lg md:text-xl text-gray-400 leading-relaxed max-w-2xl mx-auto">
            Ask <strong className="text-gray-200 font-semibold">questions</strong>.
          </p>
          <p className="text-lg md:text-xl text-gray-400 leading-relaxed max-w-2xl mx-auto mb-12">
            Book a <strong className="text-gray-200 font-semibold">meeting</strong> with us.
          </p>
        </div>

        {/* Phone number */}
        <div
          className={`inline-flex flex-col items-center gap-4 transition-all duration-1000 delay-400 ease-[cubic-bezier(0.22,1,0.36,1)] ${
            revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {/* Premium pill button */}
          <a href="tel:+918442976946" className="relative inline-block">
            {/* Pulsing outer glow ring */}
            <span className="absolute inset-0 rounded-full bg-green-400/20 blur-lg animate-ping" style={{ animationDuration: '2.4s' }} />
            <span className="absolute -inset-1 rounded-full bg-green-400/10 blur-md animate-pulse" style={{ animationDuration: '1.8s' }} />
            <div className="relative flex items-center gap-2.5 bg-green-400 text-gray-900 px-6 py-3 rounded-full font-medium text-sm animate-button-breathe cursor-pointer hover:scale-105 hover:bg-green-300 transition-transform duration-300">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-line text-lg"></i>
              </div>
              <span>Tap to call</span>
            </div>
          </a>

          <span className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white tracking-tight leading-none select-text">
            +91 84429 76946
          </span>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   PAGE ROOT
───────────────────────────────────────────── */
export default function RealEstatePage() {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <HeroSection />
      <DemoVideoSection />
      <StatementHookSection />
      <StatementContextSection />
      <StatementBridgeSection />
      <CoreOfferingsSection />
      <SetupSection />
      <PricingSection />
      <REContactSection />
      <FAQSection />
      <Footer />
    </div>
  );
}